import { COLORS } from './format';
import { useCandidatePanel } from './CandidatePanelContext';

/**
 * The 📋 chip: what the last interview round said, and the way into the candidate panel.
 *
 * It wears the verdict itself — "Hire", or "Hire · No hire" when a two-person panel disagreed —
 * so the question a recruiter opens a card to ask is already answered before any click. Theresa,
 * Oct 2026: latest round only, both words when they differ, one when they agree. The words come
 * from the board payload's one verdict map, not from a request per chip.
 *
 * Clicking opens the panel with the full written feedback. That is ONE click, deliberately: the
 * chip is not a shortcut into a "full picture" screen that then needs a second click to reach the
 * feedback — the two most common things to want are the verdict and the words behind it.
 *
 * Renders on every card, with or without feedback, for the reason the notes chip does: the
 * candidate nobody has written about yet is the one you most need a way in to. Without a verdict
 * it is a quiet grey 📋.
 *
 * A plain <button>, not the DS `Button`, to sit beside 📝 and ✉️ and look like them.
 * Outside a CandidatePanelProvider it renders nothing.
 */

/**
 * Green for hire, red for no hire, amber when the panel split.
 *
 * NOT `ratingChipStyle`: that one colours by the LEADING DIGIT of a Lever label ("3 - Hire") and
 * calls anything without a digit yellow. This chip shows the word, and a split panel here is two
 * words rather than the string "mixed".
 */
function verdictStyle(verdicts: string[]): { bg: string; text: string } {
  if (verdicts.length > 1) return { bg: COLORS.orangeBg, text: COLORS.orangeText };
  const word = verdicts[0] || '';
  // "No hire" contains "hire", so the negative has to be tested first.
  if (/no\s*hire/i.test(word)) return { bg: COLORS.redBg, text: COLORS.redText };
  if (/hire/i.test(word)) return { bg: COLORS.greenBg, text: COLORS.greenText };
  return { bg: COLORS.blueBg, text: COLORS.blueText };
}

export function FeedbackChip({
  candidateName,
  opportunityId,
}: {
  candidateName: string;
  opportunityId: string;
}) {
  const api = useCandidatePanel();
  if (!api) return null;
  const verdicts = api.verdictsFor(opportunityId);
  const style = verdicts.length
    ? verdictStyle(verdicts)
    : { bg: COLORS.cotton, text: '#767676' };
  return (
    <button
      type="button"
      onClick={() => api.openPanel({ opportunityId, candidateName })}
      className="inline-flex shrink-0 items-center gap-1 whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-semibold hover:opacity-80"
      style={{ backgroundColor: style.bg, color: style.text }}
      title={
        verdicts.length
          ? `Last round: ${verdicts.join(' · ')} — click for the written feedback`
          : 'Interview feedback'
      }
      aria-label={
        verdicts.length
          ? `Interview feedback for ${candidateName} — last round ${verdicts.join(' and ')}`
          : `Interview feedback for ${candidateName}`
      }
    >
      📋{verdicts.length > 0 && <span>{verdicts.join(' · ')}</span>}
    </button>
  );
}
